import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarClock, RotateCcw } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'
import { formatDate } from '@/lib/date'
import type { Order, OrderLifecycle, RescheduleQuote } from '@/types'
import Button from '@/components/ui/Button'
import { Field, Textarea } from '@/components/ui/Field'
import Modal from '@/components/ui/Modal'
import { useToast } from '@/components/ui/toast'
import CapacityCalendar from '@/components/order/CapacityCalendar'
import { invalidateOrderViews } from '@/components/order/lifecycle'

type Tab = 'reschedule' | 'replan'

/**
 * Two ways to change when an order is made, one dialog. Moving the production
 * date is for an order the floor has not touched: the new day must have room
 * and every section is booked again. Re-planning is for work already under
 * way: the sections left are booked again from today. The server says which
 * one applies; a tab that does not shows why.
 */
export default function RescheduleModal({
  order,
  lifecycle,
  onClose,
}: {
  order: Order | null
  lifecycle?: OrderLifecycle
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<Tab>('reschedule')
  const [month, setMonth] = useState('')
  const [date, setDate] = useState<string | null>(null)
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (!order) return
    setTab(lifecycle?.reschedule.allowed ? 'reschedule' : 'replan')
    setMonth(order.production_date.slice(0, 7))
    setDate(null)
    setReason('')
    // Reset only when the dialog opens on an order, not on every refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id])

  const quote = useQuery({
    queryKey: ['reschedule-quote', order?.id, date],
    queryFn: async () =>
      (await api.get<{ data: RescheduleQuote }>(`/admin/orders/${order!.id}/reschedule/quote`, { params: { date } })).data.data,
    enabled: Boolean(order && date) && tab === 'reschedule',
    staleTime: 0,
  })

  const save = useMutation({
    mutationFn: async () =>
      tab === 'reschedule'
        ? (await api.post<{ message?: string }>(`/admin/orders/${order!.id}/reschedule`, {
            production_date: date,
            reason: reason.trim() || null,
          })).data
        : (await api.post<{ message?: string }>(`/admin/orders/${order!.id}/replan`, { reason: reason.trim() })).data,
    onSuccess: (res) => {
      invalidateOrderViews(queryClient)
      onClose()
      toast.success(tab === 'reschedule' ? 'Production date moved' : 'Re-planned', res.message)
    },
    onError: (err) => toast.error(tab === 'reschedule' ? 'Could not move the order' : 'Could not re-plan', errorMessage(err)),
  })

  const verdict = lifecycle?.[tab]
  const ready =
    Boolean(verdict?.allowed) &&
    (tab === 'reschedule' ? Boolean(date) && quote.data?.allowed === true : reason.trim().length > 0)

  return (
    <Modal
      open={Boolean(order)}
      onClose={onClose}
      width="max-w-xl"
      title={order ? `Schedule - ${order.number}` : ''}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button disabled={!ready} loading={save.isPending} onClick={() => save.mutate()}>
            {tab === 'reschedule' ? (
              <>
                <CalendarClock className="size-4" /> Move order
              </>
            ) : (
              <>
                <RotateCcw className="size-4" /> Re-plan
              </>
            )}
          </Button>
        </>
      }
    >
      {order && (
        <div>
          <div className="mb-4 grid grid-cols-2 gap-1 rounded-lg bg-foreground/5 p-1 text-sm">
            {(['reschedule', 'replan'] as Tab[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                aria-pressed={tab === t}
                className={cn(
                  'rounded-md px-2 py-2 font-medium',
                  tab === t ? 'bg-background text-ink shadow-sm' : 'text-muted hover:text-ink',
                )}
              >
                {t === 'reschedule' ? 'Move production date' : 'Re-plan sections'}
              </button>
            ))}
          </div>

          {!verdict?.allowed ? (
            <p className="rounded-lg bg-app p-3 text-sm text-muted">{verdict?.reason ?? 'Not available for this order.'}</p>
          ) : tab === 'reschedule' ? (
            <div className="space-y-3">
              <p className="text-xs text-muted">
                Every section is booked again from the new date, against the work already on the floor. The
                customer is emailed the new dates.
              </p>
              <CapacityCalendar
                month={month}
                onMonthChange={setMonth}
                value={date}
                onChange={setDate}
                pieces={order.total_pieces}
                countsQuota={order.counts_capacity ?? true}
                currentDate={order.production_date}
                heading={
                  <p className="text-sm">
                    Now <span className="font-semibold">{formatDate(order.production_date)}</span>, {order.total_pieces} pcs
                  </p>
                }
              />
              {date && (
                <div
                  className={cn(
                    'rounded-lg border p-2.5 text-sm',
                    quote.data && !quote.data.allowed ? 'border-danger/60 bg-danger/20 text-danger' : 'border-line bg-app',
                  )}
                >
                  {quote.isError ? (
                    errorMessage(quote.error)
                  ) : !quote.data ? (
                    'Checking the day...'
                  ) : quote.data.allowed ? (
                    <>
                      Delivery due moves from <span className="font-bold">{formatDate(quote.data.current_etd)}</span> to{' '}
                      <span className="font-bold text-status-success">{formatDate(quote.data.etd)}</span>
                      <span className="block text-2xs text-muted">
                        From the work booked in each section right now. Confirmed when you move it.
                      </span>
                    </>
                  ) : (
                    quote.data.reason
                  )}
                </div>
              )}
              <Field label="Reason (optional)" htmlFor="reschedule-reason">
                <Textarea
                  id="reschedule-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={500}
                  placeholder="e.g. Customer asked for a later date"
                />
              </Field>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-muted">
                Books the sections this job has not finished again from today, against current load, and moves the
                delivery due date to match. The customer is emailed only if it moves later.
              </p>
              <Field label="Reason" htmlFor="replan-reason">
                <Textarea
                  id="replan-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={500}
                  placeholder="e.g. Print machine down for two days"
                />
              </Field>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}
